import {
  estimatePercentReward,
  hasVisibleCooperationOffer,
} from "../lib/cooperationOffer";

const PERSONAL_OWNER_DISABLED_NOTE =
  "Винагорода партнеру вимкнена — інші ріелтори її не бачать.";

/**
 * Property detail cooperation offer surface.
 * Visible offer → percent/fixed + estimate + optional terms.
 * Disabled note when offer exists with enabled=false and either personal
 * `isOwner` or an explicit `disabledNote` (corporate / non-owner wording).
 * Null offer → render nothing.
 */
function DetailCooperationBlock({
  property,
  isOwner = false,
  disabledNote = null,
}) {
  if (!property) return null;

  const offer = property.cooperation_offer;
  if (offer == null) return null;

  if (!hasVisibleCooperationOffer(offer)) {
    const note =
      typeof disabledNote === "string" && disabledNote.trim()
        ? disabledNote.trim()
        : isOwner
          ? PERSONAL_OWNER_DISABLED_NOTE
          : null;
    if (!note || offer.enabled !== false) return null;
    return (
      <section
        className="detail-cooperation"
        aria-label="Винагорода партнеру"
        data-testid="detail-cooperation-owner-disabled"
      >
        <div className="container">
          <div className="row detail-page-align-row">
          <div
            className="detail-cooperation__card detail-cooperation__card--disabled"
            role="note"
          >
            <div className="detail-cooperation__icon" aria-hidden="true">
              <i className="fa-solid fa-handshake-slash" />
            </div>
            <div className="detail-cooperation__content">
              <h2 className="detail-cooperation__title">Винагорода партнеру</h2>
              <p className="detail-cooperation__disabled-note">{note}</p>
            </div>
          </div>
          </div>
        </div>
      </section>
    );
  }

  let rewardLine = null;
  let estimateLine = null;
  if (offer.type === "percent") {
    const percent = Number(offer.value);
    if (Number.isFinite(percent)) {
      const pctLabel = `${percent.toLocaleString("uk-UA", {
        maximumFractionDigits: 2,
      })}%`;
      rewardLine = pctLabel;
      const estimate = estimatePercentReward(offer, property.price);
      if (estimate != null && property.currency) {
        const formatted = estimate
          .toLocaleString("uk-UA", { maximumFractionDigits: 2 })
          .replace(/\u00a0/g, " ");
        estimateLine = `~${formatted} ${property.currency}`;
      }
    }
  } else if (offer.type === "fixed") {
    const amount = Number(offer.value);
    if (Number.isFinite(amount)) {
      const formatted = amount
        .toLocaleString("uk-UA", { maximumFractionDigits: 2 })
        .replace(/\u00a0/g, " ");
      const currency = offer.currency || "";
      rewardLine = `${formatted}${currency ? ` ${currency}` : ""}`;
    }
  }

  if (!rewardLine) return null;

  const terms =
    offer.terms && String(offer.terms).trim()
      ? String(offer.terms).trim()
      : null;

  // Keep a single combined line for tests/consumers that match "% … USD".
  const rewardDisplay = estimateLine
    ? `${rewardLine} (${estimateLine})`
    : rewardLine;

  return (
    <section
      className="detail-cooperation"
      aria-label="Винагорода партнеру"
      data-testid="detail-cooperation-offer"
    >
      <div className="container">
        <div className="row detail-page-align-row">
        <div className="detail-cooperation__card">
          <div className="detail-cooperation__icon" aria-hidden="true">
            <i className="fa-solid fa-handshake" />
          </div>
          <div className="detail-cooperation__content">
            <h2 className="detail-cooperation__title">Винагорода партнеру</h2>
            <p className="detail-cooperation__reward feature-deals-list__cell-numeric">
              {rewardDisplay}
            </p>
            {terms ? (
              <p className="detail-cooperation__terms">{terms}</p>
            ) : null}
          </div>
        </div>
        </div>
      </div>
    </section>
  );
}

export default DetailCooperationBlock;
